// Elevation computations for 3D terrain -- shared by TerrainTiles, TerrainWater,
// SelectionRing, MoveTargetRings, Infrastructure, Units, and Cities.
import type { SceneNode } from '@aod/protocol';
import { levelSurfaceOffset } from './terrainLevels.js';

/** Biome -- base hex surface height in world units. */
export const BIOME_BASE_HEIGHT: Record<string, number> = {
  plains: 0,
  desert: 0.05,
  forest: 0.05,
  tundra: 0.05,
  hills: 0.12,
  mountain: 0.3,
  coast: -0.1,
  river: -0.05,
  ocean: -0.25,
};

const SIDES: Array<[number, number]> = [
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
];

/** Simple hash from two integers -- 0..1 deterministic float. */
function hash2d(q: number, r: number): number {
  const n = Math.sin(q * 127.1 + r * 311.7) * 43758.5453;
  return n - Math.floor(n);
}

/** Per-hex elevation modifier from (q, r). Deterministic, no jitter. */
export function hexNoise(q: number, r: number): number {
  return hash2d(q, r);
}

/**
 * Smooth value noise in [0,1): bilinear interpolation of hashed lattice
 * corners with smoothstep easing. Low frequency => neighbouring props share
 * a tint (groves, dry patches) rather than each picking independently.
 */
export function smoothField(x: number, z: number): number {
  const x0 = Math.floor(x);
  const z0 = Math.floor(z);
  const fx = x - x0;
  const fz = z - z0;
  const sx = fx * fx * (3 - 2 * fx);
  const sz = fz * fz * (3 - 2 * fz);
  const a = hash2d(x0, z0);
  const b = hash2d(x0 + 1, z0);
  const c = hash2d(x0, z0 + 1);
  const d = hash2d(x0 + 1, z0 + 1);
  return a + (b - a) * sx + (c - a) * sz + (a - b - c + d) * sx * sz;
}

/** Total elevation for a hex coordinate given its biome. */
export function hexElevation(q: number, r: number, biome: string): number {
  const base = BIOME_BASE_HEIGHT[biome] ?? 0;
  const noise = hexNoise(q, r);
  switch (biome) {
    case 'mountain':
      // Plateau 0.30..0.50; the peak itself is TILE_RELIEF (reliefAt). Kept
      // low so mountain tiles don't tower over and hide neighbouring towns.
      return base + noise * 0.2;
    case 'hills':
      return base + noise * 0.08;
    case 'desert':
      return base + noise * 0.06;
    case 'ocean': {
      // Ocean base height is set by caller based on depth classification.
      // Default shallow: -0.25. Deep (4+ ocean neighbors): -0.55.
      return base;
    }
    case 'coast':
      return base + noise * 0.04;
    default:
      return base + noise * 0.03;
  }
}

const HEX_DIRS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
];

/** How many of the six neighbours are mountains — the massif signal. */
export function mountainNeighbourCount(
  q: number,
  r: number,
  biomeMap: Map<string, string>,
): number {
  let n = 0;
  for (const [dq, dr] of HEX_DIRS) {
    if (biomeMap.get(`${q + dq},${r + dr}`) === 'mountain') n++;
  }
  return n;
}

/**
 * Extra height for mountains in massifs: 0 for isolated/paired peaks, then
 * ~0.22 per neighbour beyond 2 (3→0.22 … 6→0.88) — the heart of a mountain
 * range towers over its outposts and crosses the snow line.
 */
export function mountainClusterBoost(q: number, r: number, biomeMap: Map<string, string>): number {
  const n = mountainNeighbourCount(q, r, biomeMap);
  return n >= 3 ? 0.22 * (n - 2) : 0;
}

/** Build a lookup map coordKey -- biome from the tile array. */
export function buildBiomeMap(tiles: ReadonlyArray<SceneNode>): Map<string, string> {
  const map = new Map<string, string>();
  for (const t of tiles) {
    const props = t.props as Record<string, unknown> | undefined;
    const q = props?.q as number | undefined;
    const r = props?.r as number | undefined;
    const biome = (props?.biome as string) ?? 'plains';
    if (q !== undefined && r !== undefined) {
      map.set(`${q},${r}`, biome);
    }
  }
  return map;
}

/** Determine if an ocean tile is deep (4+ ocean neighbors). */
export function isDeepOcean(q: number, r: number, biomeMap: Map<string, string>): boolean {
  const biome = biomeMap.get(`${q},${r}`);
  if (biome !== 'ocean') return false;
  let oceanNeighbors = 0;
  for (const [dq, dr] of SIDES) {
    const nb = biomeMap.get(`${q + dq},${r + dr}`);
    if (nb === 'ocean') oceanNeighbors++;
  }
  return oceanNeighbors >= 4;
}

const WATER_BIOMES = new Set(['ocean', 'coast', 'river']);

// ── Terrain levels (terrain-levels design) ──────────────────────────────
// Sim-derived 0..=12 level per tile, when the `terrain.levels` module is on
// (snapshot prop `level`). HexGrid publishes the coord→level map here; every
// tileSurfaceY/surfaceYAt consumer re-heights automatically. `null` = no
// levels (module off) → the legacy biome-derived path below.
// Height math lives in terrainLevels.ts; this file only consumes it.
export { LEVEL_STEP, setTileLevels, terrainLevelsActive } from './terrainLevels.js';

/**
 * How far every land prism's top sits above its raw hexElevation. Keeps
 * plains (elevation 0..0.03) clearly above the coast water surface.
 */
export const LAND_TOP_LIFT = 0.08;

/** Y of the water plane TerrainWater draws for a water tile. */
export function waterSurfaceY(q: number, r: number, biomeMap: Map<string, string>): number {
  const biome = biomeMap.get(`${q},${r}`) ?? 'ocean';
  const floor =
    biome === 'ocean'
      ? isDeepOcean(q, r, biomeMap)
        ? -0.55
        : -0.25
      : biome === 'coast'
        ? -0.1
        : -0.05;
  return floor + 0.02;
}

// ── Tile top relief ─────────────────────────────────────────────────────
// Every land tile is a prism whose top rises from its plateau edge (radius
// TILE_PLATEAU, at tileSurfaceY) through a mid ring (radius 0.5) to the
// centre. TerrainTiles builds the mesh from these numbers and everything
// standing on a tile (props, buildings, units) uses reliefAt(), so nothing
// is buried inside a hill or mountain peak.

/** Tile radius: just under the hex circumradius (1) so tiles nearly touch —
 * the old 0.955 left a visible hole-seam between tiles. The hairline that
 * remains reads as a seam, and the chamfer + outlines still delineate tiles. */
export const TILE_RADIUS = 0.99;
/** Radius of the flat plateau edge; the chamfer drops from here to the rim. */
export const TILE_PLATEAU = 0.9;
/** [centre lift, mid-ring lift, mid-ring jaggedness] above the plateau. */
export const TILE_RELIEF: Record<string, readonly [number, number, number]> = {
  mountain: [0.5, 0.22, 0.1],
  hills: [0.12, 0.06, 0.04],
  desert: [0.03, 0.015, 0.02],
};
export const FLAT_RELIEF = [0.015, 0.008, 0.01] as const;

/**
 * Height above the plateau at local offset (dx, dz) from a tile's centre:
 * the smooth part of the mesh's relief (the per-corner jag is left out, so
 * props sink at most a few hundredths into it).
 */
export function reliefAt(biome: string, dx: number, dz: number): number {
  if (WATER_BIOMES.has(biome)) return 0;
  const [centre, mid] = TILE_RELIEF[biome] ?? FLAT_RELIEF;
  const d = Math.sqrt(dx * dx + dz * dz);
  if (d <= 0.5) return centre + (mid - centre) * (d / 0.5);
  if (d <= TILE_PLATEAU) return mid * (1 - (d - 0.5) / (TILE_PLATEAU - 0.5));
  return 0;
}

/**
 * Visible edge height of any tile: land plateau or water plane. TerrainTiles
 * puts each prism's plateau exactly here, and the hex outlines trace it, so
 * the grid lines follow the real tile edges.
 */
export function tileSurfaceY(q: number, r: number, biomeMap: Map<string, string>): number {
  const biome = biomeMap.get(`${q},${r}`) ?? 'plains';
  if (WATER_BIOMES.has(biome)) return waterSurfaceY(q, r, biomeMap);
  // Levelled board: height follows the sim's terrain level (smoothed, cached).
  const lv = levelSurfaceOffset(q, r);
  if (lv !== undefined) return LAND_TOP_LIFT + lv;
  const boost = biome === 'mountain' ? mountainClusterBoost(q, r, biomeMap) : 0;
  return hexElevation(q, r, biome) + LAND_TOP_LIFT + boost;
}

/**
 * Unrelieved land base height for height-gated scatter (snow line): the
 * legacy hexElevation, or the level height when levels are active.
 */
export function landBaseHeight(q: number, r: number, biome: string): number {
  return levelSurfaceOffset(q, r) ?? hexElevation(q, r, biome);
}

/** Surface Y at a point (dx, dz) from a tile's centre: plateau + relief. */
export function surfaceYAt(
  q: number,
  r: number,
  biomeMap: Map<string, string>,
  dx: number,
  dz: number,
): number {
  const biome = biomeMap.get(`${q},${r}`) ?? 'plains';
  return tileSurfaceY(q, r, biomeMap) + reliefAt(biome, dx, dz);
}

/**
 * Where something standing at a tile's centre sits: the top of the land
 * prism (including a hill or mountain peak), or the seafloor for water.
 * Units, rings, depots, ruins and markers stand on this.
 */
export function tileElevationAt(q: number, r: number, biomeMap: Map<string, string>): number {
  const biome = biomeMap.get(`${q},${r}`) ?? 'plains';
  if (biome === 'ocean') return isDeepOcean(q, r, biomeMap) ? -0.55 : -0.25;
  if (WATER_BIOMES.has(biome)) return hexElevation(q, r, biome);
  return surfaceYAt(q, r, biomeMap, 0, 0);
}
